import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, RefreshCw, LayoutDashboard, LifeBuoy, X } from 'lucide-react';
import { onStuckSpinner } from '@/lib/stuckSpinnerWatchdog';
import { useStuckEscapeActions } from '@/hooks/useStuckEscapeAction';
import FeedbackModal from '@/components/feedback/FeedbackModal';

/**
 * App-root backstop. When the watchdog reports a blocking spinner stuck past the
 * escape threshold, offer the user a way out: page-registered contextual actions
 * first, then generic Reload / Get help / (Dashboard outside onboarding). Auto-
 * dismisses when the spinner clears. Presentation only - detection lives in the
 * watchdog.
 */
export default function StuckEscapeHatch() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const contextualActions = useStuckEscapeActions();

  useEffect(() => {
    const unsub = onStuckSpinner(
      () => setVisible(true),
      () => { setVisible(false); setDismissed(false); }
    );
    return unsub;
  }, []);

  const isOnboarding = location.pathname.startsWith('/onboarding');
  const show = visible && !dismissed;
  if (!show) {
    return null;
  }

  return (
    <div
      data-stuck-watchdog="ignore"
      role="dialog"
      aria-modal="false"
      aria-labelledby="stuck-escape-title"
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9998] w-[min(28rem,calc(100vw-2rem))]"
    >
      <div className="rounded-xl border border-amber-200 dark:border-amber-900/40 bg-white dark:bg-gray-800 shadow-2xl p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <div className="flex-1 min-w-0">
            <div aria-live="assertive">
              <p id="stuck-escape-title" className="font-semibold text-gray-900 dark:text-white">Taking longer than expected</p>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
                This screen is still loading. Try one of these:
              </p>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {contextualActions.map((action) => (
                <button
                  key={action.id}
                  type="button"
                  onClick={action.onSelect}
                  title={action.description}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-primary-600 hover:bg-primary-700 text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {action.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => window.location.reload()}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  contextualActions.length === 0
                    ? 'bg-primary-600 hover:bg-primary-700 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                <RefreshCw className="w-4 h-4" aria-hidden="true" />
                Reload
              </button>
              {!isOnboarding && (
                <button
                  type="button"
                  onClick={() => { setVisible(false); navigate('/dashboard'); }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <LayoutDashboard className="w-4 h-4" aria-hidden="true" />
                  Go to Dashboard
                </button>
              )}
              <button
                type="button"
                onClick={() => setHelpOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <LifeBuoy className="w-4 h-4" aria-hidden="true" />
                Get help
              </button>
            </div>
          </div>
          {/* Dismiss suppresses re-show until the spinner fully clears (onCleared resets both flags); "Go to Dashboard" only hides now (setVisible(false)) since navigation changes the screen anyway. */}
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setDismissed(true)}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 rounded"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      <FeedbackModal
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        surface="stuck_escape"
        step={location.pathname}
        defaultSentiment="blocked"
        title="Something's stuck - tell us what happened"
      />
    </div>
  );
}
